import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiDelete } from '../api';
import { PERM, useCan } from '../permissions';

const updateBalance = (payload) => apiPost('/api/transaction/balance', payload);

const DarkModal = ({ show, onClose, children }) => {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}>
            <div style={{
                background: '#13132a',
                border: '1px solid rgba(255,77,109,0.2)',
                borderRadius: 18, padding: '28px 24px',
                width: '90%', maxWidth: 440,
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                position: 'relative'
            }}>
                <button onClick={onClose} style={{
                    position: 'absolute', top: 14, right: 18,
                    background: 'none', border: 'none',
                    color: 'rgba(255,255,255,0.4)', fontSize: 20,
                    cursor: 'pointer', lineHeight: 1
                }}>×</button>
                {children}
            </div>
        </div>
    );
};

const AllReseller = () => {
    const can = useCan();
    // The balance modal opens on the current figure, which the server withholds
    // without 'viewBalances' — so editing needs both.
    const canSeeBalance = can(PERM.VIEW_BALANCES);
    const canEditBalance = canSeeBalance && can(PERM.EDIT_BALANCE);
    const [resellers, setResellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [resellerToDelete, setResellerToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
    const [resellerToEdit, setResellerToEdit] = useState(null);
    const [newBalance, setNewBalance] = useState('');
    const [isUpdatingBalance, setIsUpdatingBalance] = useState(false);

    const fetchResellers = useCallback(async () => {
        try {
            setResellers(await apiGet('/api/admin/accounts/resellers'));
        } catch {
            setError("Failed to load resellers.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchResellers(); }, [fetchResellers]);

    const handleOpenDeleteModal = (reseller) => {
        setResellerToDelete(reseller);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setResellerToDelete(null);
    };

    const handleOpenBalanceModal = (reseller) => {
        setResellerToEdit(reseller);
        setNewBalance(reseller.Balance?.toString() || '0');
        setIsBalanceModalOpen(true);
    };

    const handleUpdateBalance = async (e) => {
        e.preventDefault();
        if (!resellerToEdit) return;
        setIsUpdatingBalance(true);
        try {
            const res = await updateBalance({ targetUid: resellerToEdit.id, targetCollection: 'resellers', newBalance: Number(newBalance) });
            if (res.success === false) throw new Error(res.message);
            alert(`Balance updated for '${resellerToEdit.name}'.`);
            setIsBalanceModalOpen(false);
            fetchResellers();
        } catch (err) {
            alert(`Failed: ${err.message}`);
        } finally {
            setIsUpdatingBalance(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!resellerToDelete) return;
        setIsDeleting(true);
        try {
            const res = await apiDelete(`/api/admin/accounts/resellers/${resellerToDelete.id}`);
            if (res.success === false) throw new Error(res.message);
            alert(`Reseller '${resellerToDelete.name}' removed.`);
            fetchResellers();
        } catch {
            alert("Failed to remove reseller.");
        } finally {
            setIsDeleting(false);
            handleCloseModal();
        }
    };

    if (loading) return (
        <div className="dark-loading">
            <div className="spinner" />
            <p>Loading All Resellers...</p>
        </div>
    );

    if (error) return <div className="dark-error">{error}</div>;

    return (
        <div className="dark-page">
            <h2>Manage All Resellers</h2>

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>User ID</th>
                            <th>Email</th>
                            {canSeeBalance && <th className="text-right">Balance</th>}
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resellers.map(reseller => (
                            <tr key={reseller.id}>
                                <td className="font-medium">{reseller.name}</td>
                                <td className="text-muted">{reseller.ID}</td>
                                <td className="text-muted">{reseller.email}</td>
                                {canSeeBalance && <td className="text-right text-green">{reseller.Balance?.toFixed(2) || "0.00"}</td>}
                                <td className="text-center">
                                    {canEditBalance && (
                                        <button
                                            className="btn-action edit"
                                            style={{ marginRight: 4 }}
                                            onClick={() => handleOpenBalanceModal(reseller)}
                                            title="Edit Balance"
                                        >
                                            <i className="fas fa-coins" />
                                        </button>
                                    )}
                                    {can(PERM.DELETE_ACCOUNTS) && (
                                        <button
                                            className="btn-action delete"
                                            onClick={() => handleOpenDeleteModal(reseller)}
                                            disabled={isDeleting}
                                            title="Remove Reseller"
                                        >
                                            <i className="fas fa-trash-alt" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {resellers.length === 0 && (
                            <tr className="empty-row"><td colSpan={canSeeBalance ? 5 : 4}>No resellers found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <DarkModal show={isBalanceModalOpen} onClose={() => setIsBalanceModalOpen(false)}>
                <h2 style={{ marginBottom: 4 }}>Edit Balance</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 20 }}>
                    Reseller: <strong style={{ color: '#fff' }}>{resellerToEdit?.name}</strong>
                </p>
                <form onSubmit={handleUpdateBalance} className="accinput">
                    <label>New Balance</label>
                    <input
                        type="number"
                        value={newBalance}
                        onChange={e => setNewBalance(e.target.value)}
                        min="0"
                        required
                        disabled={isUpdatingBalance}
                    />
                    <div className="clearfix">
                        <button type="button" className="cancelbtn" onClick={() => setIsBalanceModalOpen(false)} disabled={isUpdatingBalance}>Cancel</button>
                        <button type="submit" className="signupbtn" disabled={isUpdatingBalance}>
                            {isUpdatingBalance ? 'Updating...' : 'Save'}
                        </button>
                    </div>
                </form>
            </DarkModal>

            <DarkModal show={isModalOpen} onClose={handleCloseModal}>
                <div className="dark-confirm">
                    <div className="warn-icon"><i className="fas fa-exclamation-triangle" /></div>
                    <h3>Confirm Deletion</h3>
                    <p>
                        Are you sure you want to permanently delete reseller{' '}
                        <strong>{resellerToDelete?.name}</strong>?
                        This will remove their account and all transaction history.
                        <br /><strong style={{ color: '#fca5a5' }}>This action cannot be undone.</strong>
                    </p>
                    <div className="btn-row">
                        <button className="btn-page" onClick={handleCloseModal} disabled={isDeleting}>Cancel</button>
                        <button
                            className="btn-action delete"
                            style={{ padding: '9px 20px', fontSize: 13 }}
                            onClick={handleConfirmDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? <><i className="fas fa-spinner fa-spin" /> Deleting...</> : 'Confirm Delete'}
                        </button>
                    </div>
                </div>
            </DarkModal>
        </div>
    );
};

export default AllReseller;
